import { useMemo, useState } from 'react';

import { DateRange } from 'react-day-picker';
import { useParams } from 'react-router-dom';

import useUsers from 'api/useUsers';
import {
  DATE_DAY_MONTH_FORMAT,
  TIME_HOUR_MINUTE_FORMAT
} from 'constants/constants';
import { EFeatureFlag } from 'constants/enums';
import StatisticsView from 'views/Statistics/StatisticsView';

import DatePicker from 'components/DatePicker/DatePicker';
import { DatePickerMode } from 'components/DatePicker/DatePicker.types';
import SwitchSelector from 'components/SwitchSelector/SwitchSelector';

import { EMetric } from '../../api/useAnalytics';
import AcContentWrapper from '../../components/AcContentWrapper/AcContentWrapper';
import AcViewWrapper from '../../components/AcViewWrapper/AcViewWrapper';
import PageTopBar from '../../components/Topbar/PageTopBar';

import AnalyticsBusinessTab from './AnalyticsBusinessTab';
import AnalyticsFinanceTab from './AnalyticsFinanceTab';
import { useAnalyticsData } from './hooks/use-analytics-data';
import { useAnalyticsChartData } from './useAnalyticsChartData.hook';
import {
  DEFAULT_DATE_RANGE,
  getFixedDays,
  getWholeRange,
  isDateStringHourly
} from './utils';

const TAB_BUSINESS = 'Business';
const TAB_FINANCE = 'Finance';

const AnalyticsView: React.FC = () => {
  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_DATE_RANGE);
  const [previousDateRange, setPreviousDateRange] = useState<
    DateRange | undefined
  >();
  const [selectedTab, setSelectedTab] = useState<string>(TAB_BUSINESS);

  const { publisherId } = useParams();

  const apiDateRange = useMemo(() => getWholeRange(dateRange), [dateRange]);
  const apiPreviousDateRange = useMemo(
    () => getWholeRange(previousDateRange),
    [previousDateRange]
  );

  const { fetchFeatureFlags } = useUsers({ publisherId });
  const shouldDisplaySciPlayAnalytics =
    fetchFeatureFlags.data?.featureFlags?.[
      EFeatureFlag.CHECKOUT_SCIPLAY_ANALYTICS
    ];

  const { analytics, previousAnalytics, isLoading, filteredAnalytics } =
    useAnalyticsData(
      apiDateRange,
      apiPreviousDateRange,
      publisherId,
      shouldDisplaySciPlayAnalytics
    );

  const handleDateRangeChange = (value: DateRange) => {
    setDateRange(value);
    setPreviousDateRange(undefined);
  };

  const {
    [EMetric.TOTAL_REVENUE]: totalRevenueAnalytics,
    ...formattedAnalytics
  } = useAnalyticsChartData({
    stats: analytics.getAnalytics.data,
    trends: analytics.getTrends.data,
    previousStats: previousAnalytics.getAnalytics.data,
    previousTrends: previousAnalytics.getTrends.data,
    previousDateRange: previousDateRange,
    currentDateRange: dateRange
  });

  const xAxisDateFormat = useMemo(() => {
    const firstTrend = analytics.getTrends?.data?.[0].values?.[0];
    if (!firstTrend?.date) {
      return DATE_DAY_MONTH_FORMAT;
    }

    if (isDateStringHourly(firstTrend.date)) {
      return TIME_HOUR_MINUTE_FORMAT;
    }

    return DATE_DAY_MONTH_FORMAT;
  }, [analytics.getTrends.data]);

  const shouldDisplayNewUI =
    fetchFeatureFlags.data?.featureFlags?.[
      EFeatureFlag.DASHBOARD_ANALYTICS_NEW_UI
    ];

  if (fetchFeatureFlags.data?.featureFlags === undefined) {
    return <></>;
  }

  if (!shouldDisplayNewUI) {
    return <StatisticsView />;
  }

  return (
    <AcViewWrapper header={<PageTopBar disable={false} headline="Analytics" />} >
      <AcContentWrapper className="mb-[26px]">
        <div className="w-full mb-4 flex justify-between items-center pt-4 pb-2">
          <div className="w-[200px]">
            {shouldDisplaySciPlayAnalytics && (
              <SwitchSelector
                options={[{ value: TAB_BUSINESS }, { value: TAB_FINANCE }]}
                value={selectedTab}
                onClick={setSelectedTab}
              />
            )}
          </div>

          <div className="flex items-center w-[50%] gap-2">
            <DatePicker
              shouldDisplayTime={false}
              mode={DatePickerMode.RANGE}
              value={dateRange}
              onApply={(value) => handleDateRangeChange(value as DateRange)}
              shouldExcludeFuture
            />
            <DatePicker
              shouldDisplayTime={false}
              mode={DatePickerMode.RANGE}
              value={previousDateRange}
              onApply={(value) => setPreviousDateRange(value as DateRange)}
              onClear={() => setPreviousDateRange(undefined)}
              placeholder="Compare Dates"
              disabled={
                dateRange?.from === undefined || dateRange?.to === undefined
              }
              shouldExcludeFuture
              disableDatesAfter={dateRange.to}
              fixedRangeDays={getFixedDays(dateRange)}
            />
          </div>
        </div>
        {selectedTab === TAB_BUSINESS ? (
          <AnalyticsBusinessTab
            dateRange={dateRange}
            previousDateRange={previousDateRange}
            totalRevenueAnalytics={totalRevenueAnalytics}
            xAxisDateFormat={xAxisDateFormat}
            isLoading={isLoading}
            filteredAnalytics={filteredAnalytics.businessAnalytics}
            formattedAnalytics={formattedAnalytics}
            analytics={analytics}
            previousAnalytics={previousAnalytics}
          />
        ) : (
          <AnalyticsFinanceTab
            dateRange={dateRange}
            previousDateRange={previousDateRange}
            xAxisDateFormat={xAxisDateFormat}
            isLoading={isLoading}
            filteredAnalytics={filteredAnalytics.financeAnalytics}
            formattedAnalytics={formattedAnalytics}
            analytics={analytics}
            previousAnalytics={previousAnalytics}
          />
        )}
      </AcContentWrapper>
    </AcViewWrapper>
  );
};

export default AnalyticsView;
